import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { message } from "antd";
import { captureException } from "@sentry/nextjs";
import { isAuthenticated } from "@app/utils/auth";
import FormSkeleton from "../FormSkeleton";
import FormCompleted from "../components/FormCompleted";
import { bulkDraftResponses, bulkSubmitResponses } from "../apis";
import { useTabularData } from "./useTabularData";
import { CellValue, GridAnswers, Learner } from "./types";
import Grid from "./Grid";
import Footer from "./Footer";
import CellRenderer from "./cells/CellRenderer";
import { useGridNavigation } from "./useGridNavigation";
import { useGridResize } from "./useGridResize";
import { useAutosave } from "./useAutosave";
import { buildBulkResponses, findMissingRequiredCells } from "./payload";
import { clearCachedAnswers, storageKey } from "./storage";
import { V2Question } from "../helper";
import { QUESTION_TYPES } from "../constants";
import styles from "./TabularForm.module.scss";

// Columns whose editor owns the caret — arrows move the text caret while editing, not the grid selection.
const TEXT_LIKE_TYPES = new Set<string>([
  QUESTION_TYPES.SHORT_TEXT,
  QUESTION_TYPES.LONG_TEXT,
  QUESTION_TYPES.NUMBER,
  QUESTION_TYPES.DROPDOWN,
  QUESTION_TYPES.MULTISELECT
]);

// Tell the host how the grid finished, via postMessage to window.opener and window.parent.
const notifyHost = (action: string, extra: Record<string, unknown> = {}) => {
  if (typeof window === "undefined") return;
  const payload = { action, ...extra };
  if (window.opener) window.opener.postMessage(payload, "*");
  if (window.parent && window.parent !== window) window.parent.postMessage(payload, "*");
};

// Spreadsheet-style session feedback: rows = learners, columns = questions, saved in one grid.
const TabularForm: FunctionComponent<{ parameters: any }> = ({ parameters }) => {
  const router = useRouter();

  const firstValue = (value: unknown) => (Array.isArray(value) ? value[0] : value);
  const formId = String(firstValue(parameters.formid || parameters.formId) || "");
  const sessionId = String(firstValue(parameters.sessionid || parameters.sessionId) || "");
  const tenantId = firstValue(parameters.tenantId) as string | undefined;
  const source = firstValue(parameters.source) as string | undefined;
  // Embedded as an iframe in bi-main, which owns the chrome, so hide the standalone toolbar.
  const embedded = firstValue(parameters.embedded) === "1";

  const { loading, error, questions, learners, initialAnswers, formType, legacyId } = useTabularData({
    formId,
    sessionId,
    tenantId
  });

  // The grid's live answer state (answers[learnerId][questionId]).
  const [answers, setAnswers] = useState<GridAnswers>({});
  const [btnLoading, setBtnLoading] = useState({ draft: false, save: false });
  const [submitted, setSubmitted] = useState(false);
  // Required cells left empty on a submitted row — "{learnerId}:{questionId}". Outlined red until filled.
  const [invalidCells, setInvalidCells] = useState<Set<string>>(new Set());
  useEffect(() => {
    setAnswers(initialAnswers);
  }, [initialAnswers]);

  const cacheKey = useMemo(() => storageKey(tenantId, formId, sessionId), [tenantId, formId, sessionId]);
  // Debounced localStorage safety net (durable copy is the server draft).
  useAutosave(cacheKey, answers, !loading && !submitted);

  const updateAnswer = useCallback((learnerId: string, questionId: string, value: CellValue) => {
    setAnswers(prev => ({ ...prev, [learnerId]: { ...(prev[learnerId] || {}), [questionId]: value } }));
    // Clear this cell's required-error highlight as soon as it's edited.
    setInvalidCells(prev => {
      const key = `${learnerId}:${questionId}`;
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const renderCell = useCallback(
    // Every row stays editable; re-saving updates an existing submission.
    (question: V2Question, learner: Learner, value: CellValue) => (
      <CellRenderer
        question={question}
        value={value}
        onChange={next => updateAnswer(learner.userId, question.id, next)}
        formType={formType}
        legacyId={legacyId}
      />
    ),
    [updateAnswer, formType, legacyId]
  );

  const handleCancel = useCallback(() => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Discard your changes and close?")) return;
    // Embedded: an iframe can't close the parent tab — let the host page handle it.
    if (embedded) notifyHost("cancel");
    else window.close();
  }, [embedded]);

  const handleSaveDraft = useCallback(async () => {
    const responses = buildBulkResponses(questions, answers);
    if (!responses.length) {
      message.info("Nothing to save yet.");
      return;
    }
    try {
      setBtnLoading(prev => ({ ...prev, draft: true }));
      await bulkDraftResponses(formId, { sessionId, responses });
      notifyHost("formSavedInDraft");
      message.success("Draft saved.");
    } catch (err) {
      captureException(err);
      message.error("Could not save draft. Please try again.");
    } finally {
      setBtnLoading(prev => ({ ...prev, draft: false }));
    }
  }, [questions, answers, formId, sessionId]);

  const handleSave = useCallback(async () => {
    const responses = buildBulkResponses(questions, answers);
    if (!responses.length) {
      message.info("Please fill feedback for at least one learner.");
      return;
    }
    // Required-field check for the rows being submitted — block + outline the empty required cells.
    const missing = findMissingRequiredCells(questions, answers);
    if (missing.length) {
      setInvalidCells(new Set(missing));
      message.error("Please fill the required fields.");
      setTimeout(() => document.querySelector("[data-invalid]")?.scrollIntoView({ block: "center" }), 0);
      return;
    }
    try {
      setBtnLoading(prev => ({ ...prev, save: true }));
      const res = await bulkSubmitResponses(formId, { sessionId, responses });
      const failed = res?.data?.failed || [];
      const failedIds = failed.map((row: { givenToUserId: string }) => row.givenToUserId);
      if (failedIds.length) {
        // Partial failure: keep the grid + cached draft so the coach can retry the failed rows.
        message.warning(`Saved, but ${failedIds.length} learner(s) could not be submitted.`);
      } else {
        message.success("Feedback saved.");
        // Clear the local safety-net only on a full success — a partial failure still needs the draft.
        clearCachedAnswers(cacheKey);
      }
      // Let the host page show the success/failed-learners UI; it owns the tab, so we never close it here.
      notifyHost("formSubmitted", { failed: failedIds });
      // Standalone shows the completed screen; embedded keeps the grid for retrying failed rows.
      if (!embedded) setSubmitted(true);
    } catch (err) {
      captureException(err);
      message.error("Could not save feedback. Please try again.");
    } finally {
      setBtnLoading(prev => ({ ...prev, save: false }));
    }
  }, [questions, answers, formId, sessionId, cacheKey, embedded]);

  // Learner submissions require an authenticated session (same guard as the classic renderer).
  // When embedded, skip the redirect — useTabularData's own fetch will fail and surface an error.
  useEffect(() => {
    if (source === "user" && typeof window !== "undefined" && !isAuthenticated() && window.parent === window) {
      const parsedUrl = new URL(window.location.href);
      router.push(`/login?callbackUrl=${parsedUrl.pathname + parsedUrl.search}`);
    }
  }, [source, router]);

  // Ctrl/Cmd+S from the grid — submit just this learner's row (same validation as the full Save).
  const handleSaveRow = useCallback(
    async (rowIndex: number) => {
      const learner = learners[rowIndex];
      if (!learner) return;
      const rowAnswers: GridAnswers = { [learner.userId]: answers[learner.userId] || {} };
      const responses = buildBulkResponses(questions, rowAnswers);
      if (!responses.length) {
        message.info("Nothing to save in this row yet.");
        return;
      }
      const missing = findMissingRequiredCells(questions, rowAnswers);
      if (missing.length) {
        setInvalidCells(prev => {
          const next = new Set(prev);
          missing.forEach(cellKey => next.add(cellKey));
          return next;
        });
        message.error("Please fill the required fields.");
        setTimeout(() => document.querySelector("[data-invalid]")?.scrollIntoView({ block: "center" }), 0);
        return;
      }
      try {
        const res = await bulkSubmitResponses(formId, { sessionId, responses });
        if ((res?.data?.failed || []).length) message.warning("This row could not be submitted.");
        else message.success("Row saved.");
      } catch (err) {
        captureException(err);
        message.error("Could not save this row. Please try again.");
      }
    },
    [learners, answers, questions, formId, sessionId]
  );

  const isEditableCol = useCallback((col: number) => TEXT_LIKE_TYPES.has(questions[col]?.questionType), [questions]);
  const nav = useGridNavigation({ rowCount: learners.length, colCount: questions.length, isEditableCol, onSaveRow: handleSaveRow });
  const resize = useGridResize();

  if (loading) {
    return (
      <div className={styles.loader}>
        <FormSkeleton />
      </div>
    );
  }

  if (error) {
    return <div className={styles.message}>{error}</div>;
  }

  if (!questions.length) {
    return <div className={styles.message}>This form has no questions yet.</div>;
  }

  if (!learners.length) {
    return <div className={styles.message}>No learners found for this session.</div>;
  }

  if (submitted) {
    return <FormCompleted />;
  }

  return (
    // bi-main owns the chrome, so this renders just the grid + footer.
    <div className={styles.tabularContainer} style={resize.containerStyle}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div className={styles.gridWrap} onKeyDown={nav.handleKeyDown}>
        <Grid
          questions={questions}
          learners={learners}
          answers={answers}
          invalidCells={invalidCells}
          renderCell={renderCell}
          isActive={nav.isActive}
          onActivate={nav.activate}
          registerCell={nav.registerCell}
          onStartRowResize={resize.startRowResize}
          onStartColumnResize={resize.startColumnResize}
          getColumnWidth={resize.getColumnWidth}
          srnoWidth={resize.srnoWidth}
        />
      </div>
      <Footer onCancel={handleCancel} onSaveDraft={handleSaveDraft} onSave={handleSave} draftLoading={btnLoading.draft} saveLoading={btnLoading.save} />
    </div>
  );
};

export default TabularForm;
